// ProjectRowActions — the listing's kebab cell, carrying the same actions the
// selection bar offers: Open · Edit · Archive/Restore · Delete, always acting
// on this one row. Open is a read path and always shows; the write items are
// optional callbacks — the column factory passes them only when the matching
// capability is granted AND the row is not a pinned default (system projects
// get Open only), so absence here means hidden, matching the bar's
// hide-don't-disable rule.
//
// Open, Edit, Archive/Restore and Delete are all live: Edit opens the page's
// project drawer for this row, Archive/Restore confirms through the page's
// shared modal, and Delete opens the shared type-to-confirm dialog. Delete
// appears on the Archived tab only — a live project has to be archived before
// it can be deleted. Owns its own open state (one row's menu at a time) and
// stops clicks bubbling into the row, which navigates to the project's detail
// page.

import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';

interface ProjectRowActionsProps {
  /** Flips the Archive item into Restore on the Archived tab. */
  archived: boolean;
  /** Navigates to the project's detail page — same target a row click has. */
  onOpen: () => void;
  /** Opens the page's edit drawer on this row; absent = hidden. */
  onEdit?: () => void;
  /** Opens the page's shared archive/restore confirm for this one row. */
  onArchiveRestore?: () => void;
  /** Archived rows only — a live project must be archived first; absent = hidden. */
  onDelete?: () => void;
}

const ProjectRowActions = ({
  archived,
  onOpen,
  onEdit,
  onArchiveRestore,
  onDelete,
}: ProjectRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const stop = (e: MouseEvent) => e.stopPropagation();

  // Every item closes the menu first, so the page's modal or navigation isn't
  // left rendering underneath an open dropdown.
  const pick = (run: () => void) => (e: MouseEvent) => {
    stop(e);
    setOpen(false);
    run();
  };

  return (
    <span onClick={stop}>
      <Dropdown
        open={open}
        onClose={() => setOpen(false)}
        align='end'
        portal
        width={220}
        trigger={
          <IconButton
            icon='ellipsis-vertical'
            variant='ghost'
            size='sm'
            aria-label={t('projects_row_actions')}
            aria-expanded={open}
            onClick={(e) => {
              stop(e);
              setOpen((o) => !o);
            }}
          />
        }
      >
        <button type='button' role='menuitem' className='app-dropdown__item' onClick={pick(onOpen)}>
          <Icon name='external-link' size={16} />
          <span className='app-dropdown__item-label'>{t('projects_action_open')}</span>
        </button>
        {onEdit && (
          <button
            type='button'
            role='menuitem'
            className='app-dropdown__item'
            onClick={pick(onEdit)}
          >
            <Icon name='pencil' size={16} />
            <span className='app-dropdown__item-label'>{t('projects_action_edit')}</span>
          </button>
        )}
        {onArchiveRestore && (
          <button
            type='button'
            role='menuitem'
            className='app-dropdown__item'
            onClick={pick(onArchiveRestore)}
          >
            <Icon name={archived ? 'archive-restore' : 'archive'} size={16} />
            <span className='app-dropdown__item-label'>
              {t(archived ? 'projects_action_restore' : 'projects_action_archive')}
            </span>
          </button>
        )}
        {onDelete && (
          <>
            <div className='app-dropdown__divider' role='separator' />
            <button
              type='button'
              role='menuitem'
              className='app-dropdown__item app-dropdown__item--danger'
              onClick={pick(onDelete)}
            >
              <Icon name='trash-2' size={16} />
              <span className='app-dropdown__item-label'>{t('button_delete')}</span>
            </button>
          </>
        )}
      </Dropdown>
    </span>
  );
};

export default ProjectRowActions;
